import PropTypes from "prop-types";
import React from "react";
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";

/**
 * Reusable actions dropdown for table rows.
 * @param {Array} items - Array of { label, icon, onClick, variant?, divider? }
 * @param {string} size - 'sm' | default
 * @param {string} toggleTitle - Tooltip for the toggle button
 * @param {boolean} loading - When true, shows spinner and disables dropdown
 */
const ActionsDropdown = ({ items, size = "sm", toggleTitle = "Actions", loading = false }) => {
  const validItems = Array.isArray(items) ? items.filter((i) => i && (i.divider || i.onClick || i.href)) : [];
  if (validItems.length === 0 && !loading) return null;

  return (
    // modal={false} prevents Radix from locking body scroll / pointer-events, which otherwise
    // leaks (freezing the page) when a dropdown item opens a reactstrap Modal.
    <RadixDropdownMenu.Root modal={false}>
      <RadixDropdownMenu.Trigger asChild>
        <button
          type="button"
        className="btn btn-light btn-sm shadow-none border-0 py-1 px-2 d-flex align-items-center justify-content-center"
        style={{ minWidth: 28 }}
        title={toggleTitle}
        disabled={loading}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        ) : (
          <i className="mdi mdi-dots-vertical font-size-16" />
        )}
        </button>
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          align="end"
          sideOffset={6}
          collisionPadding={12}
          style={{
            minWidth: 180,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid rgba(0,0,0,.15)",
            borderRadius: 6,
            padding: 6,
            boxShadow: "0 0.5rem 1rem rgba(0,0,0,.175)",
          }}
        >
        {validItems.map((item, idx) => (
          <React.Fragment key={idx}>
            {item.divider && (
              <RadixDropdownMenu.Separator style={{ height: 1, background: "#e9ecef", margin: "6px 0" }} />
            )}
            {item.href ? (
              <RadixDropdownMenu.Item asChild>
                <a
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noreferrer" : undefined}
                  className={`d-flex align-items-center gap-2 text-decoration-none px-2 py-1 rounded cursor-pointer ${item.variant ? `text-${item.variant}` : "text-dark"}`}
                  style={{ cursor: "pointer" }}
                >
                  {item.icon && <i className={`mdi ${item.icon} font-size-16`} />}
                  {item.label}
                </a>
              </RadixDropdownMenu.Item>
            ) : (
              <RadixDropdownMenu.Item
                onClick={(e) => {
                  e.preventDefault();
                  item.onClick?.(e);
                }}
                className={`d-flex align-items-center gap-2 text-start w-100 border-0 bg-transparent px-2 py-1 rounded cursor-pointer ${item.variant ? `text-${item.variant}` : "text-dark"}`}
                style={{ cursor: "pointer" }}
              >
                {item.icon && <i className={`mdi ${item.icon} font-size-16`} />}
                {item.label}
              </RadixDropdownMenu.Item>
            )}
          </React.Fragment>
        ))}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
};

ActionsDropdown.propTypes = {
  loading: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func,
      href: PropTypes.string,
      external: PropTypes.bool,
      variant: PropTypes.oneOf(["primary", "success", "danger", "warning", "info"]),
      divider: PropTypes.bool,
    })
  ),
  size: PropTypes.oneOf(["sm", "md"]),
  toggleTitle: PropTypes.string,
};

export default ActionsDropdown;
